import React,{useContext,useEffect, useState, useRef,useReducer} from 'react';
import { DatabaseContext } from '../../../redux/DatabaseStore';
import { useFormik } from 'formik';
import { getRequest, postRequest } from '../../../api/ApiController';
import { SaveIcon, AddIcon, CopyIcon, PrintIcon, VerticalLineIcon, BackToFirstIcon, BackIcon, ForwardIcon, ForwardToLastIcon, FavoriteIcon, PriceIcon, SaveAndCloseIcon} from "../../svg/svgIcons";
import { Dialog } from 'primereact/dialog';
import { InfinitySpin } from  'react-loader-spinner';
import AuthContext from '../../../components/AuthContext';
import UtilContext from '../../../components/UtilContext';
import { LocaleContext } from '../../../components/LocaleContext';
import { MultiSelect } from 'primereact/multiselect';
import { useTrainingCompanies } from '../../../utils/useTrainingCompanies';

export const TRAINING_COMPANY_CERT_CATALOG_ENDPOINT = '/api/company_certificates/paged';
export const TRAINING_COMPANY_CERT_PAGE_SIZE = 200;

export function canonicalNumericId(value) {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

export function buildTrainingCompanyCertCatalogUrl(companyId, page = 1) {
    const params = new URLSearchParams();
    params.set('type', 'training');
    params.set('company_id', String(companyId));
    params.set('page', String(page));
    params.set('size', String(TRAINING_COMPANY_CERT_PAGE_SIZE));
    return `${TRAINING_COMPANY_CERT_CATALOG_ENDPOINT}?${params.toString()}`;
}

export function parseTrainingCompanyCertPagedEnvelope(response) {
    if (response == null || typeof response !== 'object' || Array.isArray(response)) return null;
    if (typeof response.status === 'number' && response.status !== 200) return null;
    if (!Array.isArray(response.data)) return null;
    const lastPage = response.last_page == null ? 1 : Number(response.last_page);
    if (!Number.isFinite(lastPage) || lastPage < 1) return null;
    return { rows: response.data, lastPage };
}

export function mapTrainingCompanyCertificateOption(row, companyId) {
    const id = canonicalNumericId(row?.id);
    if (id == null) return null;
    if (row?.company_id != null && row.company_id !== '') {
        if (canonicalNumericId(row.company_id) !== companyId) return null;
    }
    return {
        id,
        name: row?.name != null ? String(row.name) : '',
    };
}

export function mergeTrainingCompanyCertificatePages(pages, companyId) {
    const seen = new Set();
    const options = [];
    const lists = Array.isArray(pages) ? pages : [];
    for (let i = 0; i < lists.length; i += 1) {
        const rows = Array.isArray(lists[i]) ? lists[i] : [];
        for (let j = 0; j < rows.length; j += 1) {
            const option = mapTrainingCompanyCertificateOption(rows[j], companyId);
            if (option == null || seen.has(option.id)) continue;
            seen.add(option.id);
            options.push(option);
        }
    }
    return options;
}

async function fetchTrainingCompanyCertificates(companyId) {
    const first = await getRequest(buildTrainingCompanyCertCatalogUrl(companyId, 1));
    const parsed = parseTrainingCompanyCertPagedEnvelope(first);
    if (parsed == null) {
        throw new Error('invalid_paged_envelope');
    }
    const pages = [parsed.rows];
    for (let page = 2; page <= parsed.lastPage; page += 1) {
        const next = await getRequest(buildTrainingCompanyCertCatalogUrl(companyId, page));
        const nextParsed = parseTrainingCompanyCertPagedEnvelope(next);
        if (nextParsed == null) {
            throw new Error('invalid_paged_envelope');
        }
        pages.push(nextParsed.rows);
    }
    return mergeTrainingCompanyCertificatePages(pages, companyId);
}

export default function TrainingCompanyCertificateAdd({certificate}){

    const [state,dispatch] = useContext(DatabaseContext);
    const { trainingCompanies, loading: loadingCompanies } = useTrainingCompanies();
    const {user} = useContext(AuthContext);
    const {toast,openFavoriteDialog,setFastView} = useContext(UtilContext);
    const { t } = useContext(LocaleContext);
    const [closeTab,setCloseTab] = useState(false);
    const submitRef = useRef(null);
    const fileListRef = useRef(null);
    const [formCertificate,setFormCertificate] = useState(certificate);
    const [company,setCompany] = useState("");
    const [certificates,setCertificates] = useState([]);
    const [catalogStatus,setCatalogStatus] = useState('idle');

    const cacheRef = useRef(new Map());
    const inFlightRef = useRef(new Map());
    const generationRef = useRef(0);
    const mountedRef = useRef(true);

    const [insertCopyIcon,setInsertCopyIcon] = useState((user.permissions?.includes("add training-company"))?false:true);

    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    function applyIfCurrent(generation, fn) {
        if (!mountedRef.current) return;
        if (generationRef.current !== generation) return;
        fn();
    }

    function loadCertificatesForCompany(companyId, { force = false } = {}) {
        generationRef.current += 1;
        const generation = generationRef.current;

        if (companyId == null) {
            applyIfCurrent(generation, () => {
                setCertificates([]);
                setCatalogStatus('idle');
            });
            return Promise.resolve();
        }

        if (!force && cacheRef.current.has(companyId)) {
            const cached = cacheRef.current.get(companyId);
            applyIfCurrent(generation, () => {
                setCertificates(cached);
                setCatalogStatus('loaded');
            });
            return Promise.resolve(cached);
        }

        applyIfCurrent(generation, () => {
            setCertificates([]);
            setCatalogStatus('loading');
        });

        let pending = (!force && inFlightRef.current.get(companyId)) || null;
        if (!pending) {
            pending = fetchTrainingCompanyCertificates(companyId)
                .then((options) => {
                    cacheRef.current.set(companyId, options);
                    return options;
                })
                .finally(() => {
                    if (inFlightRef.current.get(companyId) === pending) {
                        inFlightRef.current.delete(companyId);
                    }
                });
            inFlightRef.current.set(companyId, pending);
        }

        return pending.then((options) => {
            applyIfCurrent(generation, () => {
                setCertificates(options);
                setCatalogStatus('loaded');
            });
            return options;
        }).catch(() => {
            applyIfCurrent(generation, () => {
                setCertificates([]);
                setCatalogStatus('error');
            });
        });
    }

    function addNewTraining(){
        dispatch({type:'create_new_tab',name: t('Common.NewCertificate'), translationKey: 'Common.NewCertificate', legacyLabel: t('Common.NewCertificate'),page:'TrainingCompanyCertificateAdd',props:{certificate: null,type:'add'},mask:'trng024'})
      }
      function copyTraining(){
        dispatch({type:'create_new_tab',name: t('Common.NewCertificate'), translationKey: 'Common.NewCertificate', legacyLabel: t('Common.NewCertificate'),page:'TrainingCompanyCertificateAdd',props:{certificate: formik.values,type:'add'},mask:'trng024'})
      }

    const validate = values => {
        const errors = {};
        return errors;
    };

    const formik = useFormik({
            initialValues: formCertificate==null?{
                start_of_validity: '',end_of_validity: '',description:'',certificate_id:''}
                :formCertificate,
            enableReinitialize:true,
            validate,
            onSubmit: values => {
                let formData = new FormData();
                for (let value in values) {
                    formData.append(value, values[value] != null ? values[value] : "");
                }
                /*for (var pair of formData.entries()) {
                    console.log(pair[0]+ ', ' + pair[1]); 
                }*/
                for(let i = 0;i<fileList.length;i++){
                    //console.log("IMAGE :"+fileList[i].name);
                    formData.append('files[]',fileList[i]);
                }
                postRequest('/api/company_cert_records', formData,true)
                .then((response) => {
                    if(closeTab)dispatch({type:'close_tab',id:state.selectedTab});
                    formik.resetForm();
                    setCompany("");
                    setCertificates([]);
                    setCatalogStatus('idle');
                    toast?.current?.show({ severity: 'info', summary: t('Common.Confirmed'), life: 3000, detail: t('Common.Toast.RecordSavedDetail') });
                    return response;
                }).catch((reason) => {
                    if(!navigator.online){
                        alert(t('ServiceTask.OfflineQueuedAlert'));
                    }
                    else{
                        alert(t('Common.Toast.ActionFailedDetail'));
                    }
                })
            },
        });

    function filterCertificates(company_id){
        const nextCompanyId = canonicalNumericId(company_id);
        setCompany(company_id == null ? "" : String(company_id));
        formik.setFieldValue('certificate_id', '');
        if (nextCompanyId == null) {
            loadCertificatesForCompany(null);
            return;
        }
        loadCertificatesForCompany(nextCompanyId);
    }

    function retrySelectedCompany() {
        const companyId = canonicalNumericId(company);
        if (companyId == null) return;
        loadCertificatesForCompany(companyId, { force: true });
    }

    const handleFileChange = (event) => {
        setFileList(event.target.files);
    };

    const catalogError = catalogStatus === 'error';
    const catalogLoaded = catalogStatus === 'loaded';
    const certificateSelectDisabled = catalogStatus === 'idle' || catalogStatus === 'loading' || catalogError;

    return ( 
        <>
            <div>
            <div className='w-full h-10 flex flex-row bg-[#333]'>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={insertCopyIcon} type='submit' onClick={() => {setCloseTab(true);submitRef.current.click();}}><SaveAndCloseIcon/></button></div>
            <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={insertCopyIcon} type='submit' onClick={() => {submitRef.current.click();}}><SaveIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => addNewTraining()}><AddIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" disabled={insertCopyIcon} onClick={() => copyTraining()}><CopyIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><VerticalLineIcon/></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button className="disabled:opacity-25" onClick={() => openFavoriteDialog(t('Common.NewTrainingCompany'),"TrainingRecordAdd")}><FavoriteIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><button onClick={() => setFastView("TrainingRecordAdd")}><PriceIcon/></button></div>
          <div className='flex flex-col w-10 h-10 px-2 py-1 justify-center '><VerticalLineIcon/></div>
        </div>
        <form onSubmit={formik.handleSubmit}>
        <div className="relative flex flex-col overflow-x min-h-[calc(100vh-177px)] bg-gray-100">
          <div className='xl:grid grid-cols-2 gap-1 mb-2 '>
            <div className="w-auto p-6 m-4 bg-white rounded-md shadow-md">
                <h1 className="text-3xl font-semibold text-left text-zinc-900">{t('Ui.R67.ZAZNAMENAT_NOVY_CERTIFIKAT')}</h1>
                    <div>
                        <label
                            htmlFor="company"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Ui.R67.Skoliacia_firma')}</label>
                        <select 
                        required
                        id="company"
                        name='company'
                        onChange={(e) => filterCertificates(e.target.value)} value={company}
                        disabled={loadingCompanies}
                        className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                          <option value="">{loadingCompanies ? t('Common.LoadingEllipsis') : t('Common.SelectTrainingCompany')}</option>
                          {trainingCompanies.map((company) => (
                            <option key={company.id} value={company.id}>{company.name}</option>
                            ))}
                        </select>
                    </div>
                        {catalogStatus === 'loading' && (
                            <div className="mt-2 flex items-center gap-2" data-testid="training-company-cert-catalog-loading">
                                <InfinitySpin width="48" />
                                <span className="text-sm text-gray-700">{t('Common.LoadingData')}</span>
                            </div>
                        )}
                        {catalogError && (
                            <div className="mt-2 rounded-md border border-red-200 bg-white p-3" data-testid="training-company-cert-catalog-error">
                                <p className="text-sm text-gray-800">{t('Common.LoadFailed')}</p>
                                <button type="button" className="mt-2 min-h-[40px] rounded-md border border-zinc-400 bg-white px-3 py-1.5 font-semibold text-zinc-900" onClick={() => retrySelectedCompany()}>
                                    {t('Common.Refresh')}
                                </button>
                            </div>
                        )}
                        {catalogLoaded && certificates.length === 0 && (
                            <p className="mt-2 text-sm text-gray-700" data-testid="training-company-cert-catalog-empty">{t('Common.NoData')}</p>
                        )}
                        <div>
                        <label
                            htmlFor="certificate_id"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Ui.R67.Certifikat_skol_firmy')}</label>
                        <select 
                        required
                        id="certificate_id"
                        name="certificate_id"
                        disabled={certificateSelectDisabled}
                        onChange={formik.handleChange} value={formik.values.certificate_id} 
                        className="bg-white block w-full  px-4 py-2 mt-2 text-grey-700 border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40">
                          <option value="">{t('Ui.R67.Vyber_certifikat')}</option>
                          {certificates.map((certificate) => (
                            <option key={certificate.id} value={certificate.id}>{certificate.name}</option>
                          ))}
                        </select>
                        </div>
                        <div>
                        <label
                            htmlFor="start_of_validity"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Ui.R67.Datum_platnosti_od')}</label>
                        <input
                            id="start_of_validity"
                            name='start_of_validity'
                            type="date" required
                            onChange={formik.handleChange} value={formik.values.start_of_validity}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="end_of_validity"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Ui.R67.Datum_platnosti_do')}</label>
                        <input
                            id="end_of_validity"
                            name='end_of_validity'
                            type="date" required
                            onChange={formik.handleChange} value={formik.values.end_of_validity}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        />
                        </div>
                        <div>
                        <label
                            htmlFor="description"
                            className="block text-md font-semibold text-gray-800"
                        >{t('Common.Note')}</label>
                        <textarea
                            name='description'
                            type="text"
                            onChange={formik.handleChange} value={formik.values.description}
                            className="block w-full  px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40"
                        >
                        </textarea>
                        </div> 
                        <div>
                        <label
                            htmlFor="files"
                            className="block text-md font-semibold text-gray-800"
                        >
                            Protokol
                        </label>
                          <input
                          accept='image/*,application/pdf'
                          name='files'
                          ref={fileListRef}
                          onChange={handleFileChange}
                          className='block w-full px-4 py-2 mt-2 text-grey-700 bg-white border rounded-md focus:border-gray-400 focus:ring-gray-300 focus:outline-none focus:ring focus:ring-opacity-40'
                          type='file'
                          multiple="multiple"
                          id='file'/>
                        </div>
                        <button type='submit' ref={submitRef}></button>
            </div>
            </div>
            </div> 
             </form>
            </div>
            </> 
            );

}
